import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  FaSearch, FaSync, FaExternalLinkAlt, FaPlus, FaCheck, FaExclamationTriangle,
  FaHome, FaAward, FaCalendarAlt, FaClock, FaCheckCircle, FaTimesCircle, FaTrash, FaEdit
} from 'react-icons/fa';
import { BiChevronDown, BiChevronUp } from 'react-icons/bi';

const API_BASE = 'https://apissc.sainikschoolcadet.com/api';

const ScholarshipTestRecords = () => {
  // Main state
  const [scholarshipTests, setScholarshipTests] = useState([]);
  const [homeAssignments, setHomeAssignments] = useState([]);
  const [attemptCounts, setAttemptCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);

  // Filters & Search
  const [searchTerm, setSearchTerm] = useState('');
  const [assignmentFilter, setAssignmentFilter] = useState('all'); // all, assigned, active_home, inactive_home, unassigned

  // Results View State
  const [selectedTest, setSelectedTest] = useState(null);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
  const [expandedTests, setExpandedTests] = useState({});

  // Assignment Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [selectedTestForModal, setSelectedTestForModal] = useState(null);
  const [existingAssignment, setExistingAssignment] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    test_id: '',
    display_order: 0,
    active_status: true,
    image: null,
    start_time: '',
    end_time: ''
  });

  const titleInputRef = useRef(null);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null);
    }, 3500);
  };

  const formatForDateTimeInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
  };

  const getValidityBadge = (item) => {
    if (!item.start_time && !item.end_time) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
          Unrestricted Time
        </span>
      );
    }

    const now = new Date();
    const start = item.start_time ? new Date(item.start_time) : null;
    const end = item.end_time ? new Date(item.end_time) : null;

    if (start && now < start) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
          Upcoming ({new Date(item.start_time).toLocaleDateString()})
        </span>
      );
    } else if (end && now >= end) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
          Ended ({new Date(item.end_time).toLocaleDateString()})
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
          Active Window
        </span>
      );
    }
  };

  // Fetch all scholarship tests, home assignments, and attempts counts in parallel
  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [testsRes, homeRes, recordsRes] = await Promise.all([
        axios.get(`${API_BASE}/Tests?type=SCHOLARSHIP_TEST`),
        axios.get(`${API_BASE}/scholarship-home`),
        axios.get(`${API_BASE}/student-test-records/type/SCHOLARSHIP_TEST`).catch(() => ({ data: { data: [] } }))
      ]);

      const testsList = testsRes.data?.data || (Array.isArray(testsRes.data) ? testsRes.data : []);
      const homeList = Array.isArray(homeRes.data) ? homeRes.data : [];
      const recordsList = recordsRes.data?.data || (Array.isArray(recordsRes.data) ? recordsRes.data : []);

      // Calculate attempt counts per test_id and title
      const countsMap = {};
      recordsList.forEach(rec => {
        if (rec.test_id) {
          countsMap[rec.test_id] = (countsMap[rec.test_id] || 0) + 1;
        }
      });
      setAttemptCounts(countsMap);
      setHomeAssignments(homeList);

      // Create a map of home assignments by test_id
      const homeMap = {};
      homeList.forEach(item => {
        homeMap[item.test_id] = item;
      });

      // Combine tests and include any home assignments whose test_id might not be in the type assignment list
      const combinedTestsMap = {};
      testsList.forEach(t => {
        combinedTestsMap[t.test_id] = {
          ...t,
          homeAssignment: homeMap[t.test_id] || null,
          attempts_count: countsMap[t.test_id] || 0
        };
      });

      // Also ensure any test in scholarship_home is represented
      homeList.forEach(h => {
        if (!combinedTestsMap[h.test_id]) {
          combinedTestsMap[h.test_id] = {
            test_id: h.test_id,
            name: h.title,
            time_duration: null,
            publish_status: true,
            homeAssignment: h,
            attempts_count: countsMap[h.test_id] || 0
          };
        }
      });

      const finalTestsList = Object.values(combinedTestsMap);
      
      // Sort: Home-assigned tests first (by display_order), then unassigned by test_id DESC
      finalTestsList.sort((a, b) => {
        if (a.homeAssignment && !b.homeAssignment) return -1;
        if (!a.homeAssignment && b.homeAssignment) return 1;
        if (a.homeAssignment && b.homeAssignment) {
          return (a.homeAssignment.display_order || 0) - (b.homeAssignment.display_order || 0);
        }
        return b.test_id - a.test_id;
      });

      setScholarshipTests(finalTestsList);
    } catch (err) {
      console.error('Error fetching scholarship tests:', err);
      setError(err.message || 'Failed to load scholarship tests.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  // Fetch test records for selected test
  const fetchTestRecords = async (testItem) => {
    if (!testItem) return;
    
    try {
      setRecordsLoading(true);
      setSelectedTest(testItem);
      
      // Fetch by test_id first; fallback to title if needed
      let records = [];
      try {
        const response = await axios.get(`${API_BASE}/student-test-records/test/${testItem.test_id}`);
        if (response.data && response.data.success) {
          records = response.data.data || [];
        }
      } catch (e) {
        // Fallback to title query
        const encodedTitle = encodeURIComponent(testItem.name);
        const titleResponse = await axios.get(`${API_BASE}/student-test-records/title/${encodedTitle}`).catch(() => null);
        if (titleResponse && titleResponse.data && titleResponse.data.success) {
          records = titleResponse.data.data || [];
        }
      }
      
      // Get unique student IDs
      const uniqueStudentIds = [...new Set(records.map(record => record.s_id))];
      
      // Fetch student details in parallel
      const studentDetailsPromises = uniqueStudentIds.map(async (studentId) => {
        try {
          const response = await axios.get(`${API_BASE}/students/getbyid/${studentId}`);
          return { studentId, details: response.data };
        } catch (err) {
          return { studentId, details: null };
        }
      });
      
      const studentDetails = await Promise.all(studentDetailsPromises);
      const studentDetailsMap = studentDetails.reduce((acc, { studentId, details }) => {
        if (details) acc[studentId] = details;
        return acc;
      }, {});
      
      // Enrich records with student details
      const enrichedRecords = records.map(record => ({
        ...record,
        student_name: studentDetailsMap[record.s_id]?.f_name || 'Unknown',
        phone_number: studentDetailsMap[record.s_id]?.phonenumber || `ID: ${record.s_id}`,
      }));
      
      setFilteredRecords(enrichedRecords);
    } catch (err) {
      console.error('Error fetching test records:', err);
      showNotification('Failed to fetch student test records', 'error');
    } finally {
      setRecordsLoading(false);
    }
  };

  // Open Modal for Assigning / Editing Home Assignment
  const openAssignmentModal = (testItem) => {
    setSelectedTestForModal(testItem);
    const existing = testItem.homeAssignment;
    setExistingAssignment(existing);
    setFormErrors({});

    if (existing) {
      setModalMode('edit');
      setFormData({
        title: existing.title || testItem.name || '',
        test_id: existing.test_id || testItem.test_id,
        display_order: existing.display_order ?? 0,
        active_status: existing.active !== false,
        image: null,
        start_time: formatForDateTimeInput(existing.start_time),
        end_time: formatForDateTimeInput(existing.end_time)
      });
      if (existing.image_path) {
        const fullUrl = existing.image_path.startsWith('http') 
          ? existing.image_path 
          : `https://apissc.sainikschoolcadet.com/${existing.image_path}`;
        setImagePreview(fullUrl);
      } else {
        setImagePreview(null);
      }
    } else {
      setModalMode('add');
      setFormData({
        title: testItem.name || `Scholarship Test ${testItem.test_id}`,
        test_id: testItem.test_id,
        display_order: homeAssignments.length,
        active_status: true,
        image: null,
        start_time: '',
        end_time: ''
      });
      setImagePreview(null);
    }

    setIsModalOpen(true);
  };

  const closeAssignmentModal = () => {
    setIsModalOpen(false);
    setSelectedTestForModal(null);
    setExistingAssignment(null);
    setImagePreview(null);
    setFormErrors({});
  };

  const handleModalFormChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'file' 
        ? files[0] 
        : type === 'checkbox' 
          ? checked 
          : name === 'display_order' 
            ? parseInt(value, 10) || 0 
            : value
    }));

    if (type === 'file' && files[0]) {
      const reader = new FileReader();
      reader.onload = (uploadEvent) => {
        setImagePreview(uploadEvent.target.result);
      };
      reader.readAsDataURL(files[0]);
    }
  };

  const validateAssignmentForm = () => {
    const errors = {};
    if (!formData.title.trim()) {
      errors.title = 'Display Title is required';
    }
    if (!formData.test_id) {
      errors.test_id = 'Test ID is required';
    }
    if (formData.display_order < 0) {
      errors.display_order = 'Display order must be a non-negative number';
    }
    if (!existingAssignment && !formData.image) {
      errors.image = 'Banner image is required for home assignment';
    }
    if (formData.start_time && formData.end_time) {
      if (new Date(formData.end_time) <= new Date(formData.start_time)) {
        errors.end_time = 'End time must be after start time';
      }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveAssignment = async (e) => {
    e.preventDefault();
    if (!validateAssignmentForm()) return;

    setFormLoading(true);
    try {
      let imagePath = existingAssignment ? existingAssignment.image_path : '';

      // Upload image if a new one was selected
      if (formData.image) {
        const imageFormData = new FormData();
        imageFormData.append('image', formData.image);
        imageFormData.append('title', formData.title || `Scholarship Test ${formData.test_id}`);
        imageFormData.append('tag', 'scholarship-home');

        try {
          const imageRes = await axios.post(`${API_BASE}/images`, imageFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          const uploadedPath = imageRes.data?.path || imageRes.data?.image?.path || imageRes.data?.url;
          if (uploadedPath) {
            imagePath = uploadedPath;
          } else {
            throw new Error('Image path missing in response');
          }
        } catch (imgErr) {
          console.error('Error uploading image:', imgErr);
          const errorMsg = imgErr.response?.data?.message || imgErr.message || 'Failed to upload image. Please try again.';
          setFormErrors(prev => ({ ...prev, image: errorMsg }));
          showNotification(errorMsg, 'error');
          setFormLoading(false);
          return;
        }
      }

      const payload = {
        title: formData.title,
        test_id: parseInt(formData.test_id, 10),
        display_order: formData.display_order,
        active: Boolean(formData.active_status),
        start_time: formData.start_time ? new Date(formData.start_time).toISOString() : null,
        end_time: formData.end_time ? new Date(formData.end_time).toISOString() : null
      };

      if (imagePath) {
        payload.image_path = imagePath;
      }

      if (existingAssignment) {
        await axios.put(`${API_BASE}/scholarship-home/${existingAssignment.id}`, payload);
        showNotification('Home assignment updated successfully', 'success');
      } else {
        await axios.post(`${API_BASE}/scholarship-home`, payload);
        showNotification('Scholarship test assigned to Home Screen successfully', 'success');
      }

      closeAssignmentModal();
      await fetchAllData();
    } catch (err) {
      console.error('Error saving home assignment:', err);
      showNotification(err.response?.data?.message || 'Failed to save home assignment', 'error');
    } finally {
      setFormLoading(false);
    }
  };

  const handleRemoveAssignment = async (testItem) => {
    const assignment = testItem.homeAssignment;
    if (!assignment) return;

    if (window.confirm(`Are you sure you want to unassign "${testItem.name}" from the App Home Screen?`)) {
      try {
        await axios.delete(`${API_BASE}/scholarship-home/${assignment.id}`);
        showNotification('Removed from Home Screen successfully', 'success');
        await fetchAllData();
      } catch (err) {
        console.error('Error removing home assignment:', err);
        showNotification('Failed to remove home assignment', 'error');
      }
    }
  };

  // Format helpers
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const formatTimeTaken = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const countAnsweredQuestions = (questionAnswers) => {
    if (!questionAnswers) return 0;
    return Object.keys(questionAnswers).length;
  };

  const toggleExpandTest = (recordId) => {
    setExpandedTests(prev => ({
      ...prev,
      [recordId]: !prev[recordId]
    }));
  };

  // Filter tests list
  const filteredTests = scholarshipTests.filter(test => {
    const matchesSearch = 
      test.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      test.test_id?.toString().includes(searchTerm) ||
      test.homeAssignment?.title?.toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;

    if (assignmentFilter === 'assigned') {
      return Boolean(test.homeAssignment);
    } else if (assignmentFilter === 'active_home') {
      return test.homeAssignment && test.homeAssignment.active !== false;
    } else if (assignmentFilter === 'inactive_home') {
      return test.homeAssignment && test.homeAssignment.active === false;
    } else if (assignmentFilter === 'unassigned') {
      return !test.homeAssignment;
    }
    return true;
  });

  // Filter records in results view
  const getFilteredStudentRecords = () => {
    if (!studentSearchTerm) return filteredRecords;
    return filteredRecords.filter(record => 
      record.student_name?.toLowerCase().includes(studentSearchTerm.toLowerCase()) ||
      record.s_id?.toString().includes(studentSearchTerm) ||
      record.phone_number?.toLowerCase().includes(studentSearchTerm.toLowerCase())
    );
  };

  // Summary counts
  const totalTestsCount = scholarshipTests.length;
  const assignedHomeCount = scholarshipTests.filter(t => t.homeAssignment).length;
  const activeHomeCount = scholarshipTests.filter(t => t.homeAssignment && t.homeAssignment.active !== false).length;
  const totalAttemptsCount = Object.values(attemptCounts).reduce((a, b) => a + b, 0);

  return (
    <div className="p-4 md:p-8 bg-gray-50 min-h-screen">
      {/* Toast Notification */}
      {notification && (
        <div className={`fixed top-5 right-5 z-50 p-4 rounded-lg shadow-lg flex items-center gap-3 transition-all duration-300 ${
          notification.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
        }`}>
          {notification.type === 'success' ? <FaCheck className="w-5 h-5" /> : <FaExclamationTriangle className="w-5 h-5" />}
          <span className="font-medium text-sm">{notification.message}</span>
        </div>
      )}

      {/* Main Header */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-100 text-blue-700 rounded-xl">
              <FaAward className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Scholarship Tests Workspace</h1>
              <p className="text-sm text-gray-500">
                Primary hub for Scholarship Test management, Home Screen assignments, and student analytics
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={fetchAllData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition shadow-sm"
          >
            <FaSync className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Metric Cards Banner */}
      {!selectedTest && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">Total Scholarship Tests</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{totalTestsCount}</div>
            <div className="text-xs text-gray-400 mt-1">In Test Library</div>
          </div>

          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
            <div className="text-xs font-semibold uppercase tracking-wider text-green-700">Assigned on Home</div>
            <div className="text-2xl font-bold text-green-600 mt-1">{assignedHomeCount}</div>
            <div className="text-xs text-gray-400 mt-1">Configured for Mobile Home</div>
          </div>

          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
            <div className="text-xs font-semibold uppercase tracking-wider text-blue-700">Active on Home</div>
            <div className="text-2xl font-bold text-blue-600 mt-1">{activeHomeCount}</div>
            <div className="text-xs text-gray-400 mt-1">Currently live to students</div>
          </div>

          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
            <div className="text-xs font-semibold uppercase tracking-wider text-purple-700">Student Attempts</div>
            <div className="text-2xl font-bold text-purple-600 mt-1">{totalAttemptsCount}</div>
            <div className="text-xs text-gray-400 mt-1">Total across all tests</div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      {!selectedTest ? (
        <>
          {/* Search & Filter Bar */}
          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 mb-6 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Search scholarship tests by name, ID, or display title..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white"
              />
              <FaSearch className="absolute left-3.5 top-3 text-gray-400 text-sm" />
            </div>

            <div className="flex items-center gap-2 overflow-x-auto pb-1 md:pb-0">
              <button
                onClick={() => setAssignmentFilter('all')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                  assignmentFilter === 'all' 
                    ? 'bg-blue-600 text-white shadow-sm' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All Tests ({scholarshipTests.length})
              </button>
              <button
                onClick={() => setAssignmentFilter('assigned')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition flex items-center gap-1.5 ${
                  assignmentFilter === 'assigned' 
                    ? 'bg-green-600 text-white shadow-sm' 
                    : 'bg-green-50 text-green-700 hover:bg-green-100'
                }`}
              >
                <span className="w-2 h-2 rounded-full bg-green-400"></span>
                Assigned on Home ({assignedHomeCount})
              </button>
              <button
                onClick={() => setAssignmentFilter('unassigned')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                  assignmentFilter === 'unassigned' 
                    ? 'bg-gray-700 text-white shadow-sm' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Not Assigned ({totalTestsCount - assignedHomeCount})
              </button>
            </div>
          </div>

          {/* Loading / Error States */}
          {loading && (
            <div className="flex items-center justify-center h-64 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="flex flex-col items-center gap-3">
                <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-500 border-t-transparent"></div>
                <p className="text-sm text-gray-500 font-medium">Loading Scholarship Tests & Home Assignments...</p>
              </div>
            </div>
          )}

          {error && !loading && (
            <div className="bg-white rounded-xl shadow-sm border border-red-200 p-8 text-center">
              <div className="text-red-600 font-semibold mb-2">Error loading scholarship tests</div>
              <p className="text-sm text-gray-500 mb-4">{error}</p>
              <button
                onClick={fetchAllData}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
              >
                Retry
              </button>
            </div>
          )}

          {/* Tests List */}
          {!loading && !error && (
            <div className="space-y-4">
              {filteredTests.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
                  <FaAward className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <h3 className="text-base font-semibold text-gray-800">No Scholarship Tests Found</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {searchTerm || assignmentFilter !== 'all'
                      ? 'Try adjusting your search query or filter selection.'
                      : 'No tests have been assigned the SCHOLARSHIP_TEST type yet.'}
                  </p>
                </div>
              ) : (
                filteredTests.map((test) => {
                  const assignment = test.homeAssignment;
                  const isAssigned = Boolean(assignment);
                  const isActiveOnHome = isAssigned && assignment.active !== false;

                  return (
                    <div
                      key={test.test_id}
                      className={`bg-white rounded-xl shadow-sm border transition-all duration-200 hover:shadow-md ${
                        isAssigned 
                          ? isActiveOnHome 
                            ? 'border-l-4 border-l-green-500 border-gray-200' 
                            : 'border-l-4 border-l-yellow-500 border-gray-200'
                          : 'border-l-4 border-l-gray-300 border-gray-200'
                      }`}
                    >
                      <div className="p-5">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                          {/* Test Info */}
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-1.5">
                              <span className="px-2.5 py-0.5 rounded-md text-xs font-bold bg-blue-50 text-blue-700 border border-blue-100">
                                Test ID: #{test.test_id}
                              </span>

                              {test.time_duration && (
                                <span className="flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-600">
                                  <FaClock className="w-3 h-3" />
                                  {test.time_duration} mins
                                </span>
                              )}

                              {/* Home Assignment Status Badge */}
                              {isAssigned ? (
                                isActiveOnHome ? (
                                  <span className="flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                                    🟢 Assigned on Home (Order: {assignment.display_order ?? 0})
                                  </span>
                                ) : (
                                  <span className="flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                                    <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                                    🟡 Assigned — Inactive (Order: {assignment.display_order ?? 0})
                                  </span>
                                )
                              ) : (
                                <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                                  ⚪ Not Assigned on Home
                                </span>
                              )}
                            </div>

                            <h3 className="text-lg font-bold text-gray-900 leading-snug">
                              {test.name}
                            </h3>

                            {/* Home Display Details If Assigned */}
                            {isAssigned && (
                              <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-100 flex flex-wrap items-center gap-4 text-xs text-gray-600">
                                {assignment.image_path && (
                                  <img
                                    src={
                                      assignment.image_path.startsWith('http')
                                        ? assignment.image_path
                                        : `https://apissc.sainikschoolcadet.com/${assignment.image_path}`
                                    }
                                    alt="Home Banner"
                                    className="w-10 h-10 object-cover rounded-md border border-gray-200 shadow-xs"
                                  />
                                )}
                                <div>
                                  <div className="font-semibold text-gray-800">
                                    Display Title: <span className="font-normal text-gray-700">"{assignment.title}"</span>
                                  </div>
                                  <div className="flex items-center gap-2 mt-0.5">
                                    <span>Time Window:</span>
                                    {getValidityBadge(assignment)}
                                    {assignment.start_time && (
                                      <span className="text-gray-400 text-2xs">
                                        ({new Date(assignment.start_time).toLocaleString()} - {assignment.end_time ? new Date(assignment.end_time).toLocaleString() : 'No End'})
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Stats & Actions */}
                          <div className="flex flex-wrap items-center gap-2 pt-3 lg:pt-0 border-t lg:border-t-0 border-gray-100">
                            {/* Attempt Counter Badge */}
                            <div className="px-3 py-1.5 bg-purple-50 text-purple-700 border border-purple-100 rounded-lg text-xs font-semibold flex items-center gap-1.5">
                              <FaAward className="w-3.5 h-3.5" />
                              <span>{test.attempts_count || 0} Student Attempts</span>
                            </div>

                            {/* Action: View Results */}
                            <button
                              onClick={() => fetchTestRecords(test)}
                              className="px-3.5 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition shadow-xs flex items-center gap-1.5"
                            >
                              <span>View Results</span>
                            </button>

                            {/* Home Assignment Actions */}
                            {isAssigned ? (
                              <>
                                <button
                                  onClick={() => openAssignmentModal(test)}
                                  className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-50 transition shadow-xs flex items-center gap-1"
                                >
                                  <FaEdit className="text-gray-500 w-3 h-3" />
                                  <span>Edit Home</span>
                                </button>
                                <button
                                  onClick={() => handleRemoveAssignment(test)}
                                  className="px-2.5 py-1.5 text-red-600 hover:bg-red-50 rounded-lg text-xs font-medium transition"
                                  title="Unassign from Home Screen"
                                >
                                  <FaTrash className="w-3 h-3" />
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => openAssignmentModal(test)}
                                className="px-3 py-1.5 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg text-xs font-medium hover:bg-emerald-100 transition shadow-xs flex items-center gap-1.5"
                              >
                                <FaPlus className="w-2.5 h-2.5" />
                                <span>Assign to Home</span>
                              </button>
                            )}

                            {/* Link to Test in Library */}
                            <a
                              href={`/tests/${test.test_id}`}
                              target="_blank"
                              rel="noreferrer"
                              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg text-xs transition"
                              title="View Test Questions in Test Library"
                            >
                              <FaExternalLinkAlt className="w-3.5 h-3.5" />
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </>
      ) : (
        /* Results View (Drill-down for a selected test) */
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="p-4 md:p-6 border-b border-gray-100 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <button
                onClick={() => setSelectedTest(null)}
                className="inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-800 text-sm font-semibold mb-2"
              >
                ← Back to All Scholarship Tests
              </button>
              <div className="flex items-center gap-2">
                <span className="px-2.5 py-0.5 rounded-md text-xs font-bold bg-blue-100 text-blue-800">
                  Test #{selectedTest.test_id}
                </span>
                <h2 className="text-xl font-bold text-gray-900">{selectedTest.name}</h2>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Showing {filteredRecords.length} student submission records
              </p>
            </div>

            <div className="flex items-center gap-3">
              <div className="relative w-64">
                <input
                  type="text"
                  placeholder="Search students by name, phone, or ID..."
                  value={studentSearchTerm}
                  onChange={(e) => setStudentSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <FaSearch className="absolute left-3 top-2.5 text-gray-400 text-xs" />
              </div>

              <button
                onClick={() => fetchTestRecords(selectedTest)}
                disabled={recordsLoading}
                className="p-2 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg text-sm transition"
                title="Refresh results"
              >
                <FaSync className={recordsLoading ? 'animate-spin' : ''} />
              </button>
            </div>
          </div>

          {/* Results Loading */}
          {recordsLoading && (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center gap-2">
                <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-500 border-t-transparent"></div>
                <p className="text-xs text-gray-500 font-medium">Loading student submissions...</p>
              </div>
            </div>
          )}

          {/* Empty Results */}
          {!recordsLoading && getFilteredStudentRecords().length === 0 && (
            <div className="p-12 text-center">
              <div className="text-gray-400 mb-2">No student records found</div>
              <p className="text-xs text-gray-500 mb-4">
                {studentSearchTerm ? 'No students match your search.' : 'No students have attempted this test yet.'}
              </p>
              {studentSearchTerm && (
                <button
                  onClick={() => setStudentSearchTerm('')}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium"
                >
                  Clear Search
                </button>
              )}
            </div>
          )}

          {/* Records Table */}
          {!recordsLoading && getFilteredStudentRecords().length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3">S.No</th>
                    <th className="px-4 py-3">Student</th>
                    <th className="px-4 py-3">Score & Percentage</th>
                    <th className="px-4 py-3">Questions Answered</th>
                    <th className="px-4 py-3">Time Taken</th>
                    <th className="px-4 py-3">Attempt Date</th>
                    <th className="px-4 py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-sm">
                  {getFilteredStudentRecords().map((record, index) => {
                    const percentage = record.total_marks > 0 
                      ? Math.round((record.marks_scored / record.total_marks) * 100) 
                      : 0;

                    return (
                      <React.Fragment key={record.id}>
                        <tr className="hover:bg-gray-50/80 transition">
                          <td className="px-4 py-3.5 text-xs text-gray-400 font-medium">{index + 1}</td>
                          <td className="px-4 py-3.5">
                            <div className="flex items-center gap-3">
                              <div className="h-9 w-9 flex-shrink-0 bg-blue-100 text-blue-700 font-bold rounded-full flex items-center justify-center text-sm">
                                {record.student_name ? record.student_name.charAt(0).toUpperCase() : 'S'}
                              </div>
                              <div>
                                <div className="font-semibold text-gray-900">{record.student_name}</div>
                                <div className="text-xs text-gray-500">
                                  {record.phone_number && !record.phone_number.startsWith('ID:')
                                    ? record.phone_number
                                    : `Student ID: ${record.s_id}`}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3.5">
                            <div className="font-bold text-gray-900">
                              <span className={percentage >= 70 ? 'text-green-600' : percentage >= 40 ? 'text-yellow-600' : 'text-red-600'}>
                                {record.marks_scored}
                              </span>
                              <span className="text-gray-400 font-normal"> / {record.total_marks} ({percentage}%)</span>
                            </div>
                            <div className="w-28 bg-gray-200 rounded-full h-1.5 mt-1 overflow-hidden">
                              <div
                                className={`h-full rounded-full ${
                                  percentage >= 70 ? 'bg-green-500' : percentage >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                                }`}
                                style={{ width: `${Math.min(100, Math.max(0, percentage))}%` }}
                              ></div>
                            </div>
                          </td>
                          <td className="px-4 py-3.5 text-xs text-gray-600">
                            <span className="font-medium text-gray-800">{countAnsweredQuestions(record.question_answers)}</span>
                            <span className="text-gray-400"> / {record.total_marks}</span>
                          </td>
                          <td className="px-4 py-3.5 text-xs text-gray-600">
                            {formatTimeTaken(record.time_taken)}
                          </td>
                          <td className="px-4 py-3.5 text-xs text-gray-600">
                            {formatDate(record.attempted_at)}
                          </td>
                          <td className="px-4 py-3.5 text-center">
                            <div className="flex items-center justify-center gap-1.5">
                              <button
                                onClick={() => toggleExpandTest(record.id)}
                                className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition"
                                title="Expand question breakdown"
                              >
                                {expandedTests[record.id] ? (
                                  <BiChevronUp className="h-5 w-5" />
                                ) : (
                                  <BiChevronDown className="h-5 w-5" />
                                )}
                              </button>
                              <button
                                onClick={() => window.open(`/view-student/${record.s_id}`, '_blank')}
                                className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-md transition"
                                title="View Student Profile"
                              >
                                <FaExternalLinkAlt className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>

                        {expandedTests[record.id] && (
                          <tr>
                            <td colSpan="7" className="bg-gray-50/70 px-6 py-3 border-b border-gray-100">
                              <div className="flex flex-wrap gap-4 text-xs font-medium">
                                <div className="flex items-center gap-1.5 bg-green-50 text-green-800 px-2.5 py-1 rounded-md border border-green-100">
                                  <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                  <span>Correct: {record.marks_scored}</span>
                                </div>
                                <div className="flex items-center gap-1.5 bg-red-50 text-red-800 px-2.5 py-1 rounded-md border border-red-100">
                                  <span className="w-2 h-2 rounded-full bg-red-500"></span>
                                  <span>Incorrect: {Math.max(0, countAnsweredQuestions(record.question_answers) - record.marks_scored)}</span>
                                </div>
                                <div className="flex items-center gap-1.5 bg-gray-100 text-gray-700 px-2.5 py-1 rounded-md">
                                  <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                                  <span>Unanswered: {Math.max(0, record.total_marks - countAnsweredQuestions(record.question_answers))}</span>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Assignment Modal (Assign to Home / Edit Home Assignment) */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden transition-all transform">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {modalMode === 'edit' ? 'Edit Home Screen Assignment' : 'Assign Scholarship Test to Home'}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Configure how this scholarship test appears on the student mobile app Home Screen
                </p>
              </div>
              <button
                onClick={closeAssignmentModal}
                className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSaveAssignment} className="p-6 space-y-4">
              {/* Selected Test Badge */}
              <div className="p-3 bg-blue-50 rounded-xl border border-blue-100">
                <div className="text-xs font-semibold text-blue-700 uppercase">Target Test</div>
                <div className="text-sm font-bold text-blue-950 mt-0.5">
                  #{selectedTestForModal?.test_id} - {selectedTestForModal?.name}
                </div>
              </div>

              {/* Display Title */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                  Display Title on Home Screen *
                </label>
                <input
                  ref={titleInputRef}
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleModalFormChange}
                  placeholder="e.g. Scholarship Test (Class 6) 1 Feb26"
                  className={`w-full p-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none ${
                    formErrors.title ? 'border-red-500 bg-red-50/50' : 'border-gray-300'
                  }`}
                />
                {formErrors.title && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.title}</p>
                )}
              </div>

              {/* Time Window (Start / End Times) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                    Start Time (Optional)
                  </label>
                  <input
                    type="datetime-local"
                    name="start_time"
                    value={formData.start_time}
                    onChange={handleModalFormChange}
                    className="w-full p-2 border border-gray-300 rounded-lg text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                    End Time (Optional)
                  </label>
                  <input
                    type="datetime-local"
                    name="end_time"
                    value={formData.end_time}
                    onChange={handleModalFormChange}
                    className={`w-full p-2 border rounded-lg text-xs ${
                      formErrors.end_time ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {formErrors.end_time && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.end_time}</p>
                  )}
                </div>
              </div>

              {/* Display Order & Active Toggle */}
              <div className="grid grid-cols-2 gap-4 items-center">
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                    Display Order
                  </label>
                  <input
                    type="number"
                    name="display_order"
                    value={formData.display_order}
                    onChange={handleModalFormChange}
                    min="0"
                    className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <p className="text-2xs text-gray-400 mt-0.5">Lower number appears first</p>
                </div>

                <div className="pt-4">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      name="active_status"
                      checked={formData.active_status}
                      onChange={handleModalFormChange}
                      className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-semibold text-gray-800">Active on Home Screen</span>
                  </label>
                  <p className="text-2xs text-gray-400 ml-6">Toggle visibility on app</p>
                </div>
              </div>

              {/* Banner Image Upload */}
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                  Card Banner Image {modalMode === 'add' && '*'}
                </label>
                <input
                  type="file"
                  name="image"
                  onChange={handleModalFormChange}
                  accept="image/*"
                  className={`w-full text-xs p-2 border rounded-lg file:mr-3 file:py-1 file:px-2.5 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 ${
                    formErrors.image ? 'border-red-500 bg-red-50/50' : 'border-gray-300'
                  }`}
                />
                {formErrors.image && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.image}</p>
                )}

                {imagePreview && (
                  <div className="mt-2.5 p-2 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-2xs font-semibold uppercase text-gray-500 mb-1">Image Preview:</p>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-32 object-cover rounded-md border border-gray-200"
                    />
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="pt-4 border-t border-gray-100 flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  onClick={closeAssignmentModal}
                  disabled={formLoading}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formLoading}
                  className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition shadow-sm flex items-center gap-2"
                >
                  {formLoading && <FaSync className="animate-spin text-xs" />}
                  <span>{modalMode === 'edit' ? 'Save Changes' : 'Assign to Home'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScholarshipTestRecords;